import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
// RemoteScreenDemo was unused — removed to silence ESLint
import DeviceList from './DeviceList';
import DevicePage from './DevicePage';
import Layout from './Layout';
import Dashboard from '../pages/Dashboard';
import LoginPage from '../pages/Login';
import PingPage from '../pages/maintenance/Ping';
import TraceRoutePage from '../pages/maintenance/TraceRoute';
import SpeedTestPage from '../pages/maintenance/SpeedTest';
import AppDataPage from '../pages/AppData';
import LauncherConfigPage from '../pages/pushConfig/LauncherConfig';
import CleanAppDataConfigPage from '../pages/pushConfig/CleanAppDataConfig';
import PushMessageConfigPage from '../pages/pushConfig/PushMessageConfig';
import RemoteScreenViewer from '../pages/RemoteScreenViewer';
// Box removed (was unused here)
import type { UnifiedDeviceInfo } from '../types/device';
import GroupsPage from '../pages/Groups';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { setAuthToken, fetchDeviceInfo, fetchUserDetails } from '../services/api';
import { DeviceCountProvider } from '../contexts/DeviceCountContext';
import ModernLoader from './ModernLoader';

// Wrapper component to handle direct URL navigation to device detail
function DevicePageWrapper({ selectedDevice, deviceListPage, onPageChange, onSelect, onBack }: { 
  selectedDevice: UnifiedDeviceInfo | null; 
  deviceListPage: number;
  onPageChange: (p: number) => void;
  onSelect: (d: UnifiedDeviceInfo) => void;
  onBack: () => void;
}) {
  const { sn } = useParams<{ sn: string }>();
  const [device, setDevice] = React.useState<UnifiedDeviceInfo | null>(selectedDevice);
  const [loading, setLoading] = React.useState<boolean>(!selectedDevice);
  const [error, setError] = React.useState<string | null>(null);

  // Get user details to access available tenants
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details'],
    queryFn: fetchUserDetails,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Extract user tenants
  const userTenants = React.useMemo(() => {
    if (!userDetails?.userTenants) return [];
    return userDetails.userTenants.filter((ut: any) => 
      ut?.tenant?.name && (ut.tenant.name.toLowerCase() === 'tgstream' || ut.tenant.name.toLowerCase() === 'tgmaxx' || ut.tenant.name.toLowerCase() === 'tgstreamtest' || ut.tenant.name.toLowerCase() === 'tgstreamtl4kbr')
    );
  }, [userDetails]);

  React.useEffect(() => {
    // If we already have selectedDevice from state, use it
    if (selectedDevice) {
      setDevice(selectedDevice);
      setLoading(false);
      return;
    }

    // Otherwise, fetch the device info from the SN in the URL
    if (sn && userTenants.length > 0) {
      setLoading(true);
      setError(null);
      
      // Try each tenant until we find the device
      const tryTenants = async () => {
        for (const ut of userTenants) {
          try {
            const tenantName = ut.tenant.name;
            console.log(`Trying to fetch device ${sn} from tenant: ${tenantName}`);
            const data = await fetchDeviceInfo({ sn, source: tenantName });
            if (data) {
              console.log(`Successfully fetched device from tenant: ${tenantName}`);
              setDevice(data);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.log(`Device not found in tenant: ${ut.tenant.name}`);
            // Continue to next tenant
          }
        }
        // If we get here, device wasn't found in any tenant
        console.error('Device not found in any tenant');
        setError('Device not found in any accessible tenant');
        setLoading(false);
      };

      tryTenants();
    }
  }, [sn, selectedDevice, userTenants]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <ModernLoader size={48} />
      </Box>
    );
  }

  if (error || !device) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Device not found'}
        </Typography>
        <Button variant="contained" onClick={onBack}>
          Back to Device List
        </Button>
      </Box>
    );
  }

  return <DevicePage device={device} onBack={onBack} />;
}

function AppContent({ user, setUser, onLogout }: { user: string | null; setUser: (u: string | null) => void; onLogout?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isRemoteScreen = location.pathname.toLowerCase().startsWith('/remote-screen');
  const { data: userDetails } = useQuery({
    queryKey: ['user', 'details'],
    queryFn: fetchUserDetails,
    staleTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(user),
  });
  const hasTgmaxxTenant = React.useMemo(() => {
    return Boolean(userDetails?.userTenants?.some((ut: any) => ut?.tenant?.name?.toLowerCase?.() === 'tgmaxx'));
  }, [userDetails]);

  // derive selectedTab from location for sidebar highlighting
  const path = location.pathname.toLowerCase();
  let selectedTab = 'Dashboard';
  if (path.startsWith('/devices')) selectedTab = 'Devices';
  else if (path.startsWith('/groups')) selectedTab = 'Groups';
  else if (path.startsWith('/maintenance/ping')) selectedTab = hasTgmaxxTenant ? 'Maintenance/Ping' : 'Dashboard';
  else if (path.startsWith('/maintenance/traceroute')) selectedTab = hasTgmaxxTenant ? 'Maintenance/TraceRoute' : 'Dashboard';
  else if (path.startsWith('/maintenance/speedtest')) selectedTab = hasTgmaxxTenant ? 'Maintenance/SpeedTest' : 'Dashboard';
  else if (path.startsWith('/maintenance')) selectedTab = hasTgmaxxTenant ? 'Maintenance/Ping' : 'Dashboard';
  else if (path.startsWith('/push-config/launcher')) selectedTab = hasTgmaxxTenant ? 'PushConfig/Launcher' : 'Dashboard';
  else if (path.startsWith('/push-config/clean-app-data')) selectedTab = hasTgmaxxTenant ? 'PushConfig/CleanAppData' : 'Dashboard';
  else if (path.startsWith('/push-config/push-message')) selectedTab = hasTgmaxxTenant ? 'PushConfig/PushMessage' : 'Dashboard';
  else if (path.startsWith('/push-config')) selectedTab = hasTgmaxxTenant ? 'PushConfig/Launcher' : 'Dashboard';
  else if (path.startsWith('/appdata')) selectedTab = 'AppData';

  const [selectedDevice, setSelectedDevice] = React.useState<UnifiedDeviceInfo | null>(null);
  const [deviceListPage, setDeviceListPage] = React.useState<number>(1);
  const [loggedOutDialogOpen, setLoggedOutDialogOpen] = React.useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = React.useState(false);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    const handler = (e: any) => {
      try { setAuthToken(null); } catch (err) { /* ignore */ }
      try { queryClient.clear(); } catch (err) { /* ignore */ }
      setLoggedOutDialogOpen(true);
    };
    window.addEventListener('auth:logout', handler as any);
    return () => window.removeEventListener('auth:logout', handler as any);
  }, [queryClient]);

  // when Layout's logout button is clicked, open confirmation dialog
  const handleLogoutClick = () => setConfirmLogoutOpen(true);

  const performLogout = () => {
    try { localStorage.removeItem('jwt'); } catch (e) { /* ignore */ }
    try { setAuthToken(null); } catch (e) { /* ignore */ }
    try { queryClient.clear(); } catch (e) { /* ignore */ }
    setUser(null);
    setConfirmLogoutOpen(false);
    navigate('/');
  };

  const handleTabChange = (tab: string) => {
    // map tab to route
    switch (tab) {
      case 'Dashboard':
        navigate('/dashboard');
        break;
      case 'Devices':
        navigate('/devices');
        break;
      case 'Groups':
        navigate('/groups');
        break;
      case 'Maintenance/Ping':
        navigate('/maintenance/ping');
        break;
      case 'Maintenance/TraceRoute':
        navigate('/maintenance/traceroute');
        break;
      case 'Maintenance/SpeedTest':
        navigate('/maintenance/speedtest');
        break;
      case 'PushConfig/Launcher':
        navigate('/push-config/launcher');
        break;
      case 'PushConfig/CleanAppData':
        navigate('/push-config/clean-app-data');
        break;
      case 'PushConfig/PushMessage':
        navigate('/push-config/push-message');
        break;
      case 'AppData':
        navigate('/appdata');
        break;
      default:
        navigate('/dashboard');
    }
    setSelectedDevice(null);
  };

  const handleSelectDevice = (d: UnifiedDeviceInfo) => {
    setSelectedDevice(d);
    // navigate to device detail route — include SN in the path for shareable URL
    const sn = d?.sn ?? d?.id ?? '';
    navigate(`/devices/${encodeURIComponent(sn)}`);
  };

  if (isRemoteScreen) {
    return (
      <Routes>
        <Route path="/remote-screen" element={user ? <RemoteScreenViewer /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to={user ? '/dashboard' : '/'} replace />} />
      </Routes>
    );
  }

  return (
  <Layout selectedTab={selectedTab} onTabChange={handleTabChange} showSidebar={!!user} onLogout={handleLogoutClick}>
      <Routes>
        <Route path="/" element={!user ? <LoginPage onLogin={(u) => { setUser(u); navigate('/dashboard'); }} /> : <Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" replace />} />
        <Route path="/devices" element={user ? <DeviceList page={deviceListPage} onPageChange={(p) => setDeviceListPage(p)} onSelect={handleSelectDevice} /> : <Navigate to="/" replace />} />
        <Route path="/devices/:sn" element={user ? <DevicePageWrapper selectedDevice={selectedDevice} deviceListPage={deviceListPage} onPageChange={(p) => setDeviceListPage(p)} onSelect={handleSelectDevice} onBack={() => { setSelectedDevice(null); navigate('/devices'); }} /> : <Navigate to="/" replace />} />
        <Route path="/groups" element={user ? <GroupsPage onSelectDevice={handleSelectDevice} /> : <Navigate to="/" replace />} />
  <Route path="/maintenance/ping" element={user && hasTgmaxxTenant ? <PingPage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/maintenance/ping/:sn" element={user && hasTgmaxxTenant ? <PingPage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/maintenance/traceroute" element={user && hasTgmaxxTenant ? <TraceRoutePage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/maintenance/traceroute/:sn" element={user && hasTgmaxxTenant ? <TraceRoutePage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/maintenance/speedtest" element={user && hasTgmaxxTenant ? <SpeedTestPage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/maintenance/speedtest/:sn" element={user && hasTgmaxxTenant ? <SpeedTestPage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/push-config/launcher" element={user && hasTgmaxxTenant ? <LauncherConfigPage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/push-config/clean-app-data" element={user && hasTgmaxxTenant ? <CleanAppDataConfigPage /> : <Navigate to="/dashboard" replace />} />
  <Route path="/push-config/push-message" element={user && hasTgmaxxTenant ? <PushMessageConfigPage /> : <Navigate to="/dashboard" replace />} />
        <Route path="/appdata" element={user ? <AppDataPage /> : <Navigate to="/" replace />} />
        <Route path="/remote-screen" element={user ? <RemoteScreenViewer /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to={user ? '/dashboard' : '/'} replace />} />
      </Routes>
      <Dialog
        open={loggedOutDialogOpen}
        onClose={(_e, reason) => {
          // allow backdrop click and escape to dismiss and treat as logout
          if (reason === 'backdropClick' || reason === 'escapeKeyDown' || !reason) {
            setLoggedOutDialogOpen(false);
            setUser(null);
            navigate('/');
          }
        }}

        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2, backgroundColor: 'rgba(137, 179, 196, 0.97)', borderColor: '#000', borderWidth: 40 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ bgcolor: 'error.main', color: 'white', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
            <WarningAmberIcon fontSize="small" />
          </Box>
          <Typography variant="h6">Session expired</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Your session has expired or you have been logged out. Click Ok to go to the login page and continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLoggedOutDialogOpen(false); setUser(null); navigate('/'); }} color="primary" variant="contained">Ok</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmLogoutOpen} onClose={() => setConfirmLogoutOpen(false)} maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2, backgroundColor: 'rgba(137, 179, 196, 0.97)', borderColor: '#000', borderWidth: 40 } }}>
        <DialogTitle>Confirm logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLogoutOpen(false)}>Cancel</Button>
          <Button color="error" size="small" variant="contained" onClick={performLogout}>Logout</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

function App() {
  // Initialize auth state synchronously from localStorage to avoid transient
  // redirects when the app first mounts (ensures routes can check `user` immediately).
  const [user, setUser] = React.useState<string | null>(() => {
    try {
      return localStorage.getItem('jwt') ? 'stored' : null;
    } catch (e) {
      return null;
    }
  });

  const handleLogout = () => {
    try { localStorage.removeItem('jwt'); } catch (e) { /* ignore */ }
    setUser(null);
  };

  return (
    <BrowserRouter>
      <DeviceCountProvider>
        <AppContent user={user} setUser={setUser} onLogout={handleLogout} />
      </DeviceCountProvider>
    </BrowserRouter>
  );
}

export default App;